/**
 * ThemeContext (P26).
 *
 * Lifted from `sample-wireframe/client/src/contexts/ThemeContext.tsx` —
 * V1 ships light-only per M-WIRE; the switchable hook is here for the
 * Operations dashboard's optional dark mode (P29) and for users who set
 * their OS preference. Default: light.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
  switchable: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  defaultTheme?: Theme;
  switchable?: boolean;
  children: ReactNode;
}

export function ThemeProvider({
  defaultTheme = 'light',
  switchable = false,
  children,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [theme]);

  const setTheme = useCallback(
    (next: Theme) => {
      if (!switchable) return;
      setThemeState(next);
    },
    [switchable],
  );

  const toggle = useCallback(() => {
    if (!switchable) return;
    setThemeState((t) => (t === 'light' ? 'dark' : 'light'));
  }, [switchable]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggle, switchable }),
    [theme, setTheme, toggle, switchable],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
